import React, { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { useInView } from 'react-intersection-observer';

import classNames from 'classnames';

import { Livestream, Media, User } from '../../../types/__generated__';
import clientConfig from '../../client/config';
import {
  PAGES_UI_FEATURE_IMAGE,
  CAMPAIGNS_V1_FEATURE_IMAGE,
  CAMPAIGNS_V1_GALLERY_IMAGE,
} from '../../constants/imageTemplateStrings';
import PageType from '../../constants/pageTypes';
import { PageMetaContext } from '../../context';
import { useNotNullableContext } from '../../hooks';
import useBasePageQuery from '../../hooks/useBasePageQuery';
import { useFlags } from '../../hooks/useFlags';
import { useModalToggle } from '../../hooks/useModalToggle';
import getStreamVideoDetails from '../../utils/getStreamDetails';
import { onlyImages } from '../../utils/typeGuards';
import { LazyLoader } from '../LazyLoader/LazyLoader';
import StreamChat from '../StreamChat/StreamChat';
import ImageButtons from './ImageButtons/ImageButtons';

import './FeatureImage.scss';
import styles from './FeatureImage.scss';

const LightboxLazy = React.lazy(() => import('../Lightbox/Lightbox'));

interface FeatureImageProps {
  enableLightbox?: boolean;
}

type ImageDetails = {
  url: string;
  caption?: string;
};

export const FeatureImage: FunctionComponent<FeatureImageProps> = ({
  enableLightbox = false,
}) => {
  const { t } = useTranslation('common', { useSuspense: false });
  const [videoRef, inViewRef] = useInView();
  const { enableOtgVideoChat } = useFlags();
  const { pageType, isCampaignsV1 } = useNotNullableContext(PageMetaContext);

  const { ref, setOpen, isOpen, Portal } = useModalToggle();

  const basePageData = useBasePageQuery();

  if (!basePageData.data?.page) {
    return null;
  }

  const {
    hero,
    cover,
    gallery,
    legacyId,
    livestream,
    owner,
  } = basePageData.data.page;

  const DEFAULT_IMAGE =
    pageType === PageType.ONE_PAGE
      ? `${clientConfig.imageServiceUrl}page-balloons.jpg`
      : `${clientConfig.imageServiceUrl}trainer-no-branding_web.jpg`;

  let heroImageUrl = (hero?.imageUrl ||
    (cover?.__typename === 'ImageMedia' && cover?.url) ||
    DEFAULT_IMAGE) as string;
  let altText =
    hero?.imageTitle || (cover?.__typename === 'ImageMedia' && cover.alt) || '';

  //LLHM 2023
  if (
    basePageData.data.page?.relationships?.events?.nodes[0]?.legacyId ===
    '744724'
  ) {
    heroImageUrl = 'https://images.justgiving.com/image/LLHM2023_cover.png';
    altText = 'London Landmarks Half-Marathon 2023';
  }

  const galleryImages: ImageDetails[] = [
    {
      url: `${heroImageUrl}${
        isCampaignsV1 ? `?template=${CAMPAIGNS_V1_FEATURE_IMAGE}` : ''
      }`,
    },
  ];

  const mediaCollectionUrls: ImageDetails[] =
    gallery?.nodes.filter(onlyImages).map(image => {
      const imageUrl = isCampaignsV1
        ? `${image.url}?template=${CAMPAIGNS_V1_GALLERY_IMAGE}`
        : image.url;
      return { url: imageUrl, caption: image.caption ?? undefined };
    }) ?? [];

  galleryImages.push(...mediaCollectionUrls);

  if (basePageData.loading) {
    return null;
  }

  if (legacyId) {
    const streamVideoDetails = getStreamVideoDetails(
      livestream as Livestream | null | undefined,
      owner as User | null | undefined,
      cover as Media,
    );

    if (streamVideoDetails?.embeddedUrl) {
      const streamChatProps = {
        title: streamVideoDetails.title,
        streamUrl: streamVideoDetails.embeddedUrl,
        chatUrl: streamVideoDetails.embeddedChatUrl,
      };
      return (
        <div
          ref={videoRef}
          className={classNames(!inViewRef ? styles.videoPopout : '')}
        >
          <iframe
            title={`${streamVideoDetails.title}`}
            src={`${streamVideoDetails.embeddedUrl}`}
            height="400"
            width="100%"
            frameBorder="0"
            scrolling="no"
            allowFullScreen
            className={styles.stream}
            data-qa="feature-image-stream"
          />
          {streamVideoDetails.embeddedChatUrl &&
            enableOtgVideoChat &&
            inViewRef && <StreamChat {...streamChatProps} />}
        </div>
      );
    }
  }

  return (
    <>
      <div className={styles.imageWrapper}>
        <img
          className={styles.image}
          src={`${heroImageUrl}?template=${
            isCampaignsV1 ? CAMPAIGNS_V1_FEATURE_IMAGE : PAGES_UI_FEATURE_IMAGE
          }`}
          alt={altText}
          data-qa="feature-image"
        />
        {enableLightbox && (
          <button
            data-qa="feature-image-overlay"
            onClick={() => setOpen(true)}
            type="button"
            className={styles.overlayButton}
            tabIndex={-1}
          >
            <span className="jg-sr-only">
              {t('common.openGallery', 'Open the Image Gallery')}
            </span>
          </button>
        )}
        {enableLightbox && <ImageButtons onViewGallery={() => setOpen(true)} />}
      </div>
      {isOpen && (
        <Portal scrollLock focusLock background>
          <LazyLoader>
            <LightboxLazy
              ref={ref}
              onClose={() => setOpen(false)}
              images={galleryImages.map(image => ({
                original: image.url,
                thumbnail: image.url,
                description: image.caption,
              }))}
            />
          </LazyLoader>
        </Portal>
      )}
    </>
  );
};

export default FeatureImage;
